import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import Login from "./pages/Login";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import MapView from "./pages/MapView";
import Rotations from "./pages/Rotations";
import Students from "./pages/Students";
import Approvals from "./pages/Approvals";
import Settings from "./pages/Settings";
import PlacementRequests from "./pages/PlacementRequests";
import AdminManagement from "./pages/AdminManagement";
import StudentMatches from "./pages/StudentMatches";

// Components
import Sidebar, { MobileHeader } from "./components/Sidebar";
import SamChat from "./components/SamChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { token, user: userData } = response.data;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await api.post("/auth/register", userData);
    const { token, user: newUser } = response.data;
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(newUser));
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const impersonate = async (userId) => {
    const response = await api.post(`/admin/impersonate/${userId}`);
    const { token, user: userData } = response.data;
    // Store original admin token for later
    localStorage.setItem("admin_token", localStorage.getItem("token"));
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const stopImpersonation = async () => {
    const response = await api.post("/admin/stop-impersonation");
    const { token, user: userData } = response.data;
    localStorage.removeItem("admin_token");
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, impersonate, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0B0E14]">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Component to handle root path - shows landing for guests, dashboard for authenticated
const RootRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0B0E14]">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  // If user is logged in, show dashboard
  if (user) {
    return (
      <AppLayout>
        <Dashboard />
      </AppLayout>
    );
  }

  // If not logged in, show landing page
  return <LandingPage />;
};

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count")
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "new_request": return "📩";
      case "request_accepted": return "✅";
      case "request_countered": return "🔄";
      case "request_denied": return "❌";
      case "counter_accepted": return "🤝";
      case "counter_declined": return "🚫";
      default: return "🔔";
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-400 hover:text-slate-200 transition-colors"
        data-testid="notification-bell"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-[#151A23] border border-slate-700 rounded-xl shadow-2xl z-50 max-h-96 overflow-hidden">
          <div className="p-3 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-slate-100 font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-cyan-400 text-xs hover:text-cyan-300">
                Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-72">
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-slate-500">No notifications</div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => markAsRead(notif.id)}
                  className={`p-3 border-b border-slate-800 cursor-pointer hover:bg-slate-800/50 transition-colors ${!notif.read ? "bg-slate-800/30" : ""}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-lg">{getNotificationIcon(notif.type)}</span>
                    <div className="flex-1">
                      <p className={`text-sm ${!notif.read ? "text-slate-100 font-medium" : "text-slate-300"}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{notif.message}</p>
                      <p className="text-xs text-slate-600 mt-1">
                        {new Date(notif.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {!notif.read && (
                      <span className="w-2 h-2 bg-cyan-400 rounded-full"></span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AppLayout = ({ children }) => {
  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, stopImpersonation } = useAuth();
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);

  // Helper to get user's display name
  const getUserDisplayName = (u) => {
    if (u?.first_name && u?.last_name) return `${u.first_name} ${u.last_name}`;
    if (u?.first_name) return u.first_name;
    if (u?.last_name) return u.last_name;
    return u?.email || 'User';
  };

  const handleStopImpersonation = async () => {
    setStoppingImpersonation(true);
    try {
      await stopImpersonation();
      toast.success("Returned to admin account");
      window.location.href = "/admin";
    } catch (error) {
      toast.error("Failed to stop impersonation");
      setStoppingImpersonation(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#0B0E14]">
      {/* Mobile Header */}
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
      
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className="flex-1 md:ml-64 flex flex-col pt-14 md:pt-0">
        {/* Impersonation Banner */}
        {user?.is_impersonating && (
          <div className="bg-amber-500/90 text-black px-4 py-2 flex items-center justify-between z-50" data-testid="impersonation-banner">
            <div className="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="font-medium text-sm sm:text-base">
                You are acting as <strong>{getUserDisplayName(user)}</strong> ({user?.role})
              </span>
              {user?.admin_user && (
                <span className="text-amber-900 text-sm">
                  — Logged in as {getUserDisplayName(user.admin_user)}
                </span>
              )}
            </div>
            <button
              onClick={handleStopImpersonation}
              disabled={stoppingImpersonation}
              className="px-3 py-1 bg-black text-amber-500 rounded font-medium hover:bg-black/80 transition-colors disabled:opacity-50 text-sm"
              data-testid="stop-impersonation-btn"
            >
              {stoppingImpersonation ? "Returning..." : "Return to Admin"}
            </button>
          </div>
        )}
        {/* Top Header Bar - Hidden on mobile since we have MobileHeader */}
        <header className="hidden md:flex sticky top-0 z-40 bg-[#0B0E14]/95 backdrop-blur-sm border-b border-slate-800 px-6 py-3 items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">Welcome back, <span className="text-slate-200 font-medium">{getUserDisplayName(user)}</span></span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setChatOpen(!chatOpen)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10 font-medium rounded-lg transition-all duration-200 text-sm border border-cyan-500/50 hover:border-cyan-400"
              data-testid="ask-sam-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Ask Sam
            </button>
            <NotificationBell />
          </div>
        </header>
        {/* Mobile Header Actions */}
        <div className="md:hidden sticky top-14 z-30 bg-[#0B0E14]/95 backdrop-blur-sm border-b border-slate-800 px-4 py-2 flex items-center justify-end gap-2">
          <button
            onClick={() => setChatOpen(!chatOpen)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10 font-medium rounded-lg transition-all duration-200 text-xs border border-cyan-500/50 hover:border-cyan-400"
            data-testid="ask-sam-btn-mobile"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Sam
          </button>
          <NotificationBell />
        </div>
        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
      <SamChat isOpen={chatOpen} onToggle={() => setChatOpen(!chatOpen)} showToggleButton={false} />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#151A23',
              color: '#F1F5F9',
              border: '1px solid #334155',
            },
          }}
        />
        <Routes>
          {/* Public Routes */}
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          
          {/* Root path - Landing for guests, Dashboard for authenticated users */}
          <Route path="/" element={<RootRedirect />} />
          
          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/map"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <MapView />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/rotations"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Rotations />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/students"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Students />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/approvals"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Approvals />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Settings />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/requests"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <PlacementRequests />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <AdminManagement />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/:studentId/matches"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <StudentMatches />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/welcome" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
